import React, { useState, useEffect } from 'react';
import { supabase } from '../../../../supabaseClient';
import ResponsiveDataView from '../../../common/ResponsiveDataView';
import {
  Box,
  Typography,
  Paper,
  Container,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Alert,
  Snackbar,
  Grid,
  CircularProgress,
  InputAdornment,
  Divider,
  Stack,
  Chip,
  Card,
  CardContent,
  useTheme,
  alpha
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  PeopleAlt as PeopleAltIcon,
  Close as CloseIcon,
  Business as BusinessIcon,
  Badge as BadgeIcon,
  LocationOn as LocationOnIcon,
  Description as DescriptionIcon,
  Percent as PercentIcon,
  Save as SaveIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';

const Suppliers = () => {
  const theme = useTheme();
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  
  // Form State
  const [formData, setFormData] = useState({
    name: '',
    tin: '',
    address: '',
    zip_code: '',
    atc_2306: '',
    nature_of_payment_2306: '',
    atc_2307: '',
    nature_of_payment_2307: '',
    tax_rate_2306: '0',
    tax_rate_2307: '0'
  });

  // Notification State
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  useEffect(() => {
    fetchSuppliers();
  }, []);

  const fetchSuppliers = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('suppliers')
        .select('*')
        .order('name');
      
      if (searchTerm) {
        query = query.ilike('name', `%${searchTerm}%`);
      }

      const { data, error } = await query;
      
      if (error) throw error;
      setSuppliers(data || []);
    } catch (error) {
      console.error('Error fetching suppliers:', error);
      showNotification('Error fetching suppliers', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (supplier = null) => {
    if (supplier) {
      setEditingSupplier(supplier);
      setFormData({
        name: supplier.name,
        tin: supplier.tin || '',
        address: supplier.address || '',
        zip_code: supplier.zip_code || '',
        atc_2306: supplier.atc_2306 || '',
        nature_of_payment_2306: supplier.nature_of_payment_2306 || '',
        atc_2307: supplier.atc_2307 || '',
        nature_of_payment_2307: supplier.nature_of_payment_2307 || '',
        tax_rate_2306: supplier.tax_rate_2306 || '0',
        tax_rate_2307: supplier.tax_rate_2307 || '0'
      });
    } else {
      setEditingSupplier(null);
      setFormData({
        name: '',
        tin: '',
        address: '',
        zip_code: '',
        atc_2306: '',
        nature_of_payment_2306: '',
        atc_2307: '',
        nature_of_payment_2307: '',
        tax_rate_2306: '0',
        tax_rate_2307: '0'
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingSupplier(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async () => {
    if (!formData.name) {
      showNotification('Supplier Name is required', 'warning');
      return;
    }

    // Sanitize data
    const payload = {
      name: formData.name,
      tin: formData.tin,
      address: formData.address,
      zip_code: formData.zip_code,
      atc_2306: formData.atc_2306,
      nature_of_payment_2306: formData.nature_of_payment_2306,
      atc_2307: formData.atc_2307,
      nature_of_payment_2307: formData.nature_of_payment_2307,
      tax_rate_2306: formData.tax_rate_2306 === '' ? 0 : parseFloat(formData.tax_rate_2306),
      tax_rate_2307: formData.tax_rate_2307 === '' ? 0 : parseFloat(formData.tax_rate_2307)
    };

    console.log('Submitting payload:', payload);

    try {
      if (editingSupplier) {
        // Update
        const { error } = await supabase
          .from('suppliers')
          .update(payload)
          .eq('id', editingSupplier.id);
        
        if (error) throw error;
        showNotification('Supplier updated successfully', 'success');
      } else {
        // Insert
        const { error } = await supabase
          .from('suppliers')
          .insert([payload])
          .select();
        
        if (error) throw error;
        showNotification('Supplier added successfully', 'success');
      }
      
      handleCloseDialog();
      fetchSuppliers();
    } catch (error) {
      console.error('Error saving supplier:', error);
      showNotification('Error saving supplier: ' + (error.message || 'Unknown error'), 'error');
    }
  };

  const testConnection = async () => {
    try {
      const { count, error } = await supabase
        .from('suppliers')
        .select('*', { count: 'exact', head: true });
      
      if (error) throw error;
      showNotification(`Connection successful! Total suppliers: ${count}`, 'success');
    } catch (error) {
      console.error('Connection test failed:', error);
      showNotification('Connection failed: ' + error.message, 'error');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this supplier?')) {
      try {
        const { error } = await supabase
          .from('suppliers')
          .delete()
          .eq('id', id);
        
        if (error) throw error;
        showNotification('Supplier deleted successfully', 'success');
        fetchSuppliers();
      } catch (error) {
        console.error('Error deleting supplier:', error);
        showNotification('Error deleting supplier', 'error');
      }
    }
  };

  const showNotification = (message, severity) => {
    setNotification({
      open: true,
      message,
      severity
    });
  };

  const handleCloseNotification = () => {
    setNotification(prev => ({ ...prev, open: false }));
  };

  return (
    <Container maxWidth={false} sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3, minHeight: '80vh' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <PeopleAltIcon sx={{ fontSize: 40, mr: 2, color: 'primary.main' }} />
            <Typography variant="h4" component="h1">
              Supplier Management
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Button 
              onClick={testConnection} 
              color="inherit" 
              sx={{ mr: 2 }}
            >
              Test Connection
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
            >
              Add Supplier
            </Button>
          </Box>
        </Box>

        {/* Search */}
        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search suppliers..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && fetchSuppliers()}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: (
                <Button onClick={fetchSuppliers}>Search</Button>
              )
            }}
          />
        </Box>

        {/* Responsive Suppliers Data View */}
        <ResponsiveDataView
          columns={[
            {
              key: 'name',
              label: 'Name',
              priority: 'high',
              render: (supplier) => (
                <Typography variant="body2" fontWeight="bold">
                  {supplier.name}
                </Typography>
              )
            },
            {
              key: 'tin',
              label: 'TIN',
              priority: 'high',
              render: (supplier) => supplier.tin || '—'
            },
            {
              key: 'address',
              label: 'Address',
              priority: 'medium',
              render: (supplier) => (
                <>
                  {supplier.address}
                  {supplier.zip_code && `, ${supplier.zip_code}`}
                </>
              )
            },
            {
              key: 'atc_2306',
              label: 'ATC 2306',
              priority: 'low',
              render: (supplier) => supplier.atc_2306 || '—'
            },
            {
              key: 'atc_2307',
              label: 'ATC 2307',
              priority: 'low',
              render: (supplier) => supplier.atc_2307 || '—'
            },
            {
              key: 'tax_rate_2306',
              label: '2306 %',
              priority: 'medium',
              render: (supplier) => `${supplier.tax_rate_2306 || 0}%`
            },
            {
              key: 'tax_rate_2307',
              label: '2307 %',
              priority: 'medium',
              render: (supplier) => `${supplier.tax_rate_2307 || 0}%`
            },
            {
              key: 'actions',
              label: 'Actions',
              priority: 'high',
              align: 'center',
              render: (supplier) => (
                <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                  <IconButton 
                    color="primary" 
                    size="small" 
                    onClick={() => handleOpenDialog(supplier)}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton 
                    color="error" 
                    size="small" 
                    onClick={() => handleDelete(supplier.id)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              )
            }
          ]}
          data={suppliers}
          loading={loading}
          loadingMessage="Loading registered suppliers..."
          emptyMessage="No suppliers found."
          rowKey="id"
        />
      </Paper>

      {/* Add/Edit Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: { 
            borderRadius: 3,
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ 
          bgcolor: 'primary.main', 
          color: 'white', 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          py: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {editingSupplier ? <EditIcon /> : <AddIcon />}
            <Typography variant="h6" fontWeight="bold">
              {editingSupplier ? 'Edit Supplier' : 'Add New Supplier'}
            </Typography>
          </Box>
          <IconButton onClick={handleCloseDialog} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3, bgcolor: '#f8f9fa' }}>
          <Stack spacing={3} sx={{ mt: 1 }}>
            
            {/* Basic Info Section */}
            <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: '1px solid', borderColor: 'divider', bgcolor: 'white' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 1 }}>
                <BusinessIcon color="primary" />
                <Typography variant="subtitle1" fontWeight="bold" color="text.primary">
                  Basic Information
                </Typography>
              </Box>
              
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    name="name"
                    label="Supplier Name"
                    fullWidth
                    required
                    variant="outlined"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Enter registered business name"
                  />
                </Grid>
                <Grid item xs={12} sm={8}>
                  <TextField
                    name="address"
                    label="Registered Address"
                    fullWidth
                    variant="outlined"
                    value={formData.address}
                    onChange={handleInputChange}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LocationOnIcon color="action" fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    name="zip_code"
                    label="Zip Code"
                    fullWidth
                    variant="outlined"
                    value={formData.zip_code}
                    onChange={handleInputChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="tin"
                    label="Tax Identification Number (TIN)"
                    fullWidth
                    variant="outlined"
                    value={formData.tin}
                    onChange={handleInputChange}
                    placeholder="000-000-000-000"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <BadgeIcon color="action" fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              </Grid>
            </Paper>

            {/* Tax Configuration Section */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
                <DescriptionIcon color="primary" />
                <Typography variant="subtitle1" fontWeight="bold" color="text.primary">
                  Tax Configuration
                </Typography>
              </Box>
              
              <Grid container spacing={2}>
                {/* 2306 Column */}
                <Grid item xs={12} md={6}>
                  <Paper 
                    elevation={0} 
                    sx={{ 
                      p: 2, 
                      borderRadius: 2, 
                      height: '100%',
                      border: '1px solid',
                      borderColor: alpha(theme.palette.info.main, 0.3),
                      bgcolor: alpha(theme.palette.info.main, 0.05)
                    }}
                  >
                    <Stack spacing={2}>
                      <Chip 
                        label="BIR Form 2306" 
                        color="info" 
                        size="small" 
                        sx={{ alignSelf: 'flex-start', fontWeight: 'bold' }} 
                      />
                      <TextField
                        name="atc_2306"
                        label="ATC"
                        fullWidth
                        size="small"
                        value={formData.atc_2306}
                        onChange={handleInputChange}
                        helperText="Alphanumeric Tax Code"
                        variant="outlined"
                        sx={{ bgcolor: 'white' }}
                      />
                      <TextField
                        name="nature_of_payment_2306"
                        label="Nature of Payment"
                        fullWidth
                        size="small"
                        multiline
                        rows={2}
                        value={formData.nature_of_payment_2306}
                        onChange={handleInputChange}
                        variant="outlined"
                        sx={{ bgcolor: 'white' }}
                      />
                      <TextField
                        name="tax_rate_2306"
                        label="Tax Rate"
                        type="number"
                        fullWidth
                        size="small"
                        value={formData.tax_rate_2306}
                        onChange={handleInputChange}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">%</InputAdornment>,
                        }}
                        inputProps={{ step: "0.01" }}
                        variant="outlined"
                        sx={{ bgcolor: 'white' }}
                      />
                    </Stack>
                  </Paper>
                </Grid>

                {/* 2307 Column */}
                <Grid item xs={12} md={6}>
                  <Paper 
                    elevation={0} 
                    sx={{ 
                      p: 2, 
                      borderRadius: 2, 
                      height: '100%',
                      border: '1px solid',
                      borderColor: alpha(theme.palette.success.main, 0.3),
                      bgcolor: alpha(theme.palette.success.main, 0.05)
                    }}
                  >
                    <Stack spacing={2}>
                      <Chip 
                        label="BIR Form 2307" 
                        color="success" 
                        size="small" 
                        sx={{ alignSelf: 'flex-start', fontWeight: 'bold' }} 
                      />
                      <TextField
                        name="atc_2307"
                        label="ATC"
                        fullWidth
                        size="small"
                        value={formData.atc_2307}
                        onChange={handleInputChange}
                        helperText="Alphanumeric Tax Code"
                        variant="outlined"
                        sx={{ bgcolor: 'white' }}
                      />
                      <TextField
                        name="nature_of_payment_2307"
                        label="Nature of Payment"
                        fullWidth
                        size="small"
                        multiline
                        rows={2}
                        value={formData.nature_of_payment_2307}
                        onChange={handleInputChange}
                        variant="outlined"
                        sx={{ bgcolor: 'white' }}
                      />
                      <TextField
                        name="tax_rate_2307"
                        label="Tax Rate"
                        type="number"
                        fullWidth
                        size="small"
                        value={formData.tax_rate_2307}
                        onChange={handleInputChange}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">%</InputAdornment>,
                        }}
                        inputProps={{ step: "0.01" }}
                        variant="outlined"
                        sx={{ bgcolor: 'white' }}
                      />
                    </Stack>
                  </Paper>
                </Grid>
              </Grid>
            </Box>

          </Stack>
        </DialogContent>
        
        <DialogActions sx={{ p: 3, bgcolor: '#f8f9fa', borderTop: '1px solid', borderColor: 'divider' }}>
          <Button 
            onClick={handleCloseDialog}
            variant="outlined"
            color="inherit"
            startIcon={<CancelIcon />}
            sx={{ borderRadius: 2, px: 3, borderColor: 'rgba(0,0,0,0.12)' }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained"
            startIcon={<SaveIcon />}
            sx={{ borderRadius: 2, px: 4, py: 1, boxShadow: 2 }}
          >
            Save Supplier
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notifications */}
      <Snackbar 
        open={notification.open} 
        autoHideDuration={6000} 
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseNotification} severity={notification.severity} sx={{ width: '100%' }}>
          {notification.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Suppliers;
